import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { initials, isNoPosition, isPerson, isVacancy } from '../lib/display';
import type { Facility } from '../lib/types';
import type { PersonTarget } from './Person';

const ICONS: Record<string, JSX.Element> = {
  teams: (
    <>
      <path d="M20.5 6.5a3 3 0 1 0-3-3 3 3 0 0 0 3 3z" />
      <path d="M13 8h9v7a4 4 0 0 1-4 4h-1" />
      <path d="M2 6h11v10a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4z" />
      <path d="M5.5 9.5h4M7.5 9.5V16" />
    </>
  ),
  vid: (
    <>
      <path d="m22 8-6 4 6 4V8z" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </>
  ),
  phone: (
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  ),
  sms: <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.8-.9L3 21l2-4.6A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />,
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
};

function Icon({ name, size = 17 }: { name: keyof typeof ICONS; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

const MOBILE_BREAKPOINT = 700;

/**
 * Is this a phone, where `tel:` and `sms:` are guaranteed to do something?
 *
 * On an iPhone or an Android handset both schemes are handled by the OS and the
 * buttons are unambiguously useful. On a desktop `tel:` depends on whether a
 * softphone has registered the handler — 8x8 Work does when it is installed —
 * and `sms:` almost never resolves to anything on Windows. So the two are
 * treated differently rather than shown identically everywhere:
 *
 *   • Call  — always offered. It is the button people expect, it works on a
 *             handset, and on a desktop with 8x8 Work installed it dials.
 *             Deliberately left exactly as it was; the 8x8 click-to-dial
 *             integration is a separate piece of work.
 *   • Text  — offered on a handset only. On a desktop it is replaced by
 *             Copy number, because an `sms:` link that silently does nothing is
 *             the same false affordance as the disabled Teams buttons were.
 *
 * `maxTouchPoints` catches iPadOS, which reports itself as a Mac.
 */
function isHandset(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod/i.test(ua)) return true;
  if (/iPad/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}

/**
 * Teams deep links.
 *
 * Both take a UPN in `users`. `l/chat/0/0` opens a 1:1 chat; `l/call/0/0` starts
 * a call, and `withVideo=true` makes it a video call. They open the desktop
 * client when it is installed and fall back to Teams on the web otherwise, so
 * they work for everyone in the tenant without any client detection here.
 */
const teamsChatUrl = (upn: string) =>
  `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(upn)}`;
const teamsCallUrl = (upn: string) =>
  `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(upn)}`;

export function ContactQuickActions({
  target,
  facility,
  anchor,
  onClose,
}: {
  target: PersonTarget;
  facility: Facility;
  anchor: DOMRect | null;
  onClose: () => void;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const real = isPerson(target.name);
  const hiring = isVacancy(target.name);
  const noPosition = isNoPosition(target.name);
  const ini = real ? initials(target.name!) : null;
  const tel = target.phone ? target.phone.replace(/[^\d+]/g, '') : null;

  // Lazy initializer — evaluated once, not on every render. The result is
  // derived from the user agent, so it does not change for the life of the tab.
  const [handset] = useState(isHandset);

  const dir = target.dir;
  const upn = dir?.status === 'resolved' ? dir.upn : null;
  // Where email goes, in order:
  //   1. a resolved account's mailbox — the directory's CURRENT address, and
  //      preferred over the UPN, which is not always deliverable;
  //   2. the address on the staff record, but only when nobody has been able
  //      to check it (no directory right now) — it came from the Microsoft 365
  //      people picker, so it is the best unchecked answer there is;
  //   3. nothing personal, and the site inbox below.
  // An address the directory has CHECKED and found no active account behind
  // (`inactive`, or `unknown` by email) is not offered: it would bounce under
  // a confident "Email" label.
  const mail =
    dir?.status === 'resolved' ? (dir.email ?? target.email ?? dir.upn) : !dir ? target.email : null;

  // THREE reasons Teams can be off, and they are not interchangeable.
  // `undefined` means nobody looked — the API ships an empty directory map when
  // wise-iam is not configured, or the index has not built yet. Saying "not in
  // the Entra directory" there is a specific claim about a real person that we
  // have no evidence for, which is the exact failure this change set exists to
  // stop. It gets its own wording.
  //
  // A person from `Facility_Staff` carries an email and was looked up BY it,
  // so the wording says "address", and can say something a name lookup never
  // could: that the account behind it is disabled.
  const byAddress = !!target.email;
  const teamsBlockedReason = !dir
    ? 'Directory resolution is not available right now, so Teams cannot be aimed at them yet.'
    : dir.status === 'ambiguous'
      ? `${dir.count} people in the directory share this ${byAddress ? 'address' : 'name'}, so a ` +
        `Teams message cannot be aimed at the right one from what the facility record stores.`
      : dir.status === 'inactive'
        ? `The directory account for ${target.email} is disabled, so Teams cannot reach them.`
        : byAddress
          ? `No active account in the directory matches ${target.email}, so Teams cannot be aimed at them.`
          : 'This name is not in the Entra directory, so Teams cannot be aimed at them.';

  // Anchored popover above 700px, clamped to the viewport; a bottom sheet
  // below it, which the stylesheet handles via !important overrides.
  useLayoutEffect(() => {
    const pop = popRef.current;
    if (!pop || !anchor || window.innerWidth <= MOBILE_BREAKPOINT) return;
    const pw = 322;
    const ph = pop.offsetHeight;
    let left = Math.min(anchor.right + 10, window.innerWidth - pw - 14);
    if (anchor.right + 10 + pw > window.innerWidth) left = Math.max(14, anchor.left - pw - 10);
    pop.style.left = `${left}px`;
    pop.style.top = `${Math.max(14, Math.min(anchor.top, window.innerHeight - ph - 14))}px`;
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async () => {
    if (!target.phone) return;
    try {
      await navigator.clipboard.writeText(target.phone);
      setCopied(true);
    } catch {
      /* clipboard blocked — the number is on screen anyway */
    }
  };

  return (
    <div className="qa-wrap">
      <div className="qa-bd" onClick={onClose} />
      <div className="qa-pop" ref={popRef} role="dialog" aria-label={`Contact ${target.role}`}>
        <div className="qa-hd">
          <div className={`qa-av${hiring ? ' hiring' : ''}${ini ? '' : ' none'}`}>
            {ini || (hiring ? '!' : '—')}
          </div>
          <div className="qa-id">
            <div className="qa-r">{target.role}</div>
            <div className="qa-n">
              {hiring ? 'Now Hiring' : real ? target.name : noPosition ? 'None' : 'Unassigned'}
            </div>
            <div className="qa-s">
              {facility.name} · {facility.code}
            </div>
          </div>
          <button className="qa-x" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        {!real ? (
          /* A vacant or unassigned role gets no dead call button. It says so,
             and surfaces who to escalate to from this facility's own record. */
          <>
            <div className="qa-note">
              {hiring
                ? 'This role is vacant.'
                : noPosition
                  ? 'The staff record says this facility has no such position.'
                  : 'No one is assigned to this role.'}{' '}
              Escalate to the district manager or HR.
            </div>
            <div className="qa-sec">Escalate to</div>
            <div className="qa-alt">
              <div className="qa-altr">
                <b>{facility.district || 'No district manager'}</b>
                <span>District Manager</span>
              </div>
              <div className="qa-altr">
                <b>{facility.hr || '—'}</b>
                <span>HR Manager</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="qa-grid">
              {/* Teams actions now resolve. The on-site team (PM, assistants,
                  seniors) comes from Facility_Staff with each person's Entra
                  email, and the API resolves THAT against wise-iam's mirror of
                  Entra — an exact match, and one that can tell a disabled
                  account from an unknown one. Every other role still comes from
                  Facility_Information as a display name, looked up by name.

                  They stay DISABLED — never hidden, and never aimed at a
                  guess — in the two cases where that lookup cannot answer:
                  `ambiguous` (more than one enabled account has this display
                  name, so chatting the first one would reach the wrong
                  colleague under the right name) and `unknown` (nobody in the
                  directory by that name — a contractor, a departed employee, or
                  a typo in the source row). The title says which. */}
              {upn ? (
                <>
                  <a className="qa" href={teamsChatUrl(upn)} target="_blank" rel="noreferrer">
                    <Icon name="teams" />
                    <span>Teams chat</span>
                  </a>
                  <a className="qa" href={teamsCallUrl(upn)} target="_blank" rel="noreferrer">
                    <Icon name="vid" />
                    <span>Teams call</span>
                  </a>
                </>
              ) : (
                <>
                  <button className="qa" disabled type="button" title={teamsBlockedReason}>
                    <Icon name="teams" />
                    <span>Teams chat</span>
                  </button>
                  <button className="qa" disabled type="button" title={teamsBlockedReason}>
                    <Icon name="vid" />
                    <span>Teams call</span>
                  </button>
                </>
              )}

              {/* A resolved person has a real mailbox, so mail can go to THEM.
                  Without one it goes to the site inbox, which is still the
                  right destination for reaching a facility. */}
              {mail ? (
                <a className="qa" href={`mailto:${mail}`}>
                  <Icon name="mail" />
                  <span>Email</span>
                </a>
              ) : target.siteEmail ? (
                <a className="qa" href={`mailto:${target.siteEmail}`}>
                  <Icon name="mail" />
                  <span>Email site</span>
                </a>
              ) : (
                <button className="qa" disabled type="button">
                  <Icon name="mail" />
                  <span>Email</span>
                </button>
              )}

              {/* Unchanged on purpose — 8x8 click-to-dial is separate work. */}
              {tel ? (
                <a className="qa" href={`tel:${tel}`}>
                  <Icon name="phone" />
                  <span>Call</span>
                </a>
              ) : (
                <button className="qa" disabled type="button">
                  <Icon name="phone" />
                  <span>Call</span>
                </button>
              )}

              {tel && handset && (
                <a className="qa" href={`sms:${tel}`}>
                  <Icon name="sms" />
                  <span>Text</span>
                </a>
              )}
              {tel && (
                <button className="qa" onClick={copy} type="button">
                  <Icon name="copy" />
                  <span>{copied ? 'Copied' : 'Copy number'}</span>
                </button>
              )}
            </div>
            <div className="qa-foot">
              {upn ? (
                <>
                  Teams and email reach {target.name} directly, at {mail ?? upn}.
                  {!handset && ' Text is a phone-only action; use Copy number on a desktop.'}
                </>
              ) : mail ? (
                <>
                  {teamsBlockedReason} Email goes to {mail}, the address on the staff record.
                </>
              ) : target.siteEmail ? (
                <>
                  {teamsBlockedReason} Email goes to the site inbox, which is the right
                  destination for contacting a facility.
                </>
              ) : (
                teamsBlockedReason
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
